import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { vehicleContext } from '../VehicleProvider';
import usePalette from '../../hooks/usePalette';
import InfoPopover from '../InfoPopover';
import { COLORS } from '../../styles/variables';
import {
  StatusContainer,
  Status,
  StatusDot,
  StatusTitle,
  InfoIconWrapper,
} from './VehicleStatusInfo.style';
import { IS_T3_WEBSITE } from '../../constants/environment';

const VehicleStatusInfo: React.FC = () => {
  const { t } = useTranslation();

  const { vehicleData, market } = useContext(vehicleContext);
  const nonAudiLocationFlag =
    IS_T3_WEBSITE && vehicleData?.nonAudiDealershipLocation?.multiLocation;
  const { statusTextColor, statusBgColor, popUpBgColor } = usePalette(vehicleData);
  const isCanadian = market === 'ca' || market === 'cauc';

  const vehicleStatus = useMemo(() => {
    let label = '';
    let dotColor = COLORS.flashOfOrange; // Default color
    let popoverDescription = t(
      `vdpHeader.availableSoon.availableSoonPopUpInfo${isCanadian ? 'CAN' : 'USA'}`,
    );

    if (vehicleData) {
      if (nonAudiLocationFlag) {
        label = t(`vdpHeader.nonAudiLocation`);
        dotColor = COLORS.spaceInvader;
        popoverDescription = t('vdpHeader.nonAudiLocationPopUpInfo');
      } else if (vehicleData.vehicleType === 'new' && vehicleData.saleOrderType?.includes('SOLD')) {
        label = t(`vdpHeader.reserved`);
        dotColor = COLORS.redRibbon;
        popoverDescription = t('vdpHeader.reservedPopUpInfo');
      } else if (vehicleData.isAvailableNow) {
        label = t(`vdpHeader.availableSoon.availableNow${isCanadian ? 'CAN' : 'USA'}`);
        popoverDescription = t(`vdpHeader.availableNowPopUpInfo${isCanadian ? 'CAN' : 'USA'}`);
        dotColor = COLORS.spaceInvader;
      } else if (vehicleData.vehicleOrderStatus === 'Intransit From Factory') {
        label = t('vdpHeader.availableSoon.orderStatus.factory');
      } else if (vehicleData.vehicleOrderStatus === 'Port Stock') {
        label = t('vdpHeader.availableSoon.orderStatus.port');
      } else if (vehicleData.vehicleOrderStatus === 'Intransit Inland') {
        label = t('vdpHeader.availableSoon.orderStatus.inland');
      } else if (!vehicleData.vehicleOrderStatus) {
        label = t('vdpHeader.availableSoon.orderStatus.contactDealer');
      }
    }

    return { label, dotColor, popoverDescription };
  }, [t, isCanadian, vehicleData, nonAudiLocationFlag]);

  return (
    <StatusContainer data-testid="vehicle-status-info">
      <Status backColor={statusBgColor} textColor={statusTextColor}>
        <StatusDot dotColor={vehicleStatus.dotColor} />
        <StatusTitle data-testid="status">{vehicleStatus.label}</StatusTitle>
        <InfoIconWrapper>
          <InfoPopover
            description={vehicleStatus.popoverDescription}
            iconSize={20}
            triggerTestId="popover-icon"
            iconColor={statusTextColor}
            bgColor={popUpBgColor}
          />
        </InfoIconWrapper>
      </Status>
    </StatusContainer>
  );
};

export default VehicleStatusInfo;
